import { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useLocale } from '../contexts/LocaleContext';

type AnyStyle = Record<string, any>;

const SWAP_PAIRS: Array<[string, string]> = [
  ['marginLeft', 'marginRight'],
  ['paddingLeft', 'paddingRight'],
  ['left', 'right'],
  ['borderLeftWidth', 'borderRightWidth'],
  ['borderLeftColor', 'borderRightColor'],
  ['borderTopLeftRadius', 'borderTopRightRadius'],
  ['borderBottomLeftRadius', 'borderBottomRightRadius'],
];

/**
 * Mirrors one style object for RTL:
 * - left/right margins, paddings, positions, borders and corner radii swap
 *   (physical properties — neither native nor web's dir="rtl" flips them);
 * - textAlign left/right swap, and text styles with no explicit alignment
 *   get 'right' so Arabic text starts at the reading edge;
 * - flexDirection row ↔ row-reverse on native only — on web,
 *   LocaleContext's document dir="rtl" already mirrors flex rows, matching
 *   useRTLStyles' needsFlip rule.
 */
const mirrorStyle = (style: AnyStyle, needsRowFlip: boolean): AnyStyle => {
  const out: AnyStyle = { ...style };
  for (const [a, b] of SWAP_PAIRS) {
    const hasA = a in style;
    const hasB = b in style;
    if (!hasA && !hasB) continue;
    delete out[a];
    delete out[b];
    if (hasA) out[b] = style[a];
    if (hasB) out[a] = style[b];
  }
  if (style.textAlign === 'left') out.textAlign = 'right';
  else if (style.textAlign === 'right') out.textAlign = 'left';
  else if (style.textAlign === undefined && style.fontSize !== undefined && style.alignSelf === undefined) {
    out.textAlign = 'right';
  }
  if (needsRowFlip) {
    if (style.flexDirection === 'row') out.flexDirection = 'row-reverse';
    else if (style.flexDirection === 'row-reverse') out.flexDirection = 'row';
  }
  return out;
};

/**
 * useThemedStyles + RTL mirroring in one: same `factory` contract (a
 * function returning the StyleSheet object literal, re-run per theme so
 * colors repaint), and in Arabic every style entry is mirrored (see
 * mirrorStyle). Lets an existing LTR-only screen become RTL-correct by
 * swapping its one `useThemedStyles(getStyles)` call, without touching each
 * row/margin individually. Inline `rtl.*` overrides in JSX still win, since
 * inline styles come after the sheet entry.
 */
export function useThemedRTLStyles<T extends StyleSheet.NamedStyles<T> | StyleSheet.NamedStyles<any>>(
  factory: () => T
): T {
  const { themeMode } = useTheme();
  const { isRTL } = useLocale();
  const needsRowFlip = isRTL && Platform.OS !== 'web';
  return useMemo(() => {
    const raw = factory() as AnyStyle;
    if (!isRTL) return StyleSheet.create(raw as T);
    const mirrored: AnyStyle = {};
    for (const key of Object.keys(raw)) {
      const entry = raw[key];
      mirrored[key] = entry && typeof entry === 'object' && !Array.isArray(entry) ? mirrorStyle(entry, needsRowFlip) : entry;
    }
    return StyleSheet.create(mirrored as T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMode, isRTL, needsRowFlip]);
}
